import { AppFilterDropdownContentState } from "@components/dropdown/app/AppFilterDropdownContent"
import { CollectionFilterDropdownContentState } from "@components/dropdown/collection/CollectionFilterDropdownContent"
import { ContentLabelFilterDropdownContentState } from "@components/dropdown/content-label/ContentLabelFilterDropdownContent"
import { ProductFilterDropdownContentState } from "@components/dropdown/product/ProductFilterDropdownContent"
import { SelectOption } from "@disco-ui/select/DiscoSelect"
import useDebounce from "@utils/hook/useDebounce"
import { ValueOf } from "@utils/typeUtils"
import React, { useState } from "react"

type UseTableStateArgs<T> = {
  initialActivePage?: number
  initialToolbarState: T
}

type DiscoTableOrderByOption = {
  field: string
  direction?: string | null
}

export type DiscoTableFilterByOption<T = string | number> = {
  title: string
  value: T | null
} | null

export namespace DiscoTableUtils {
  export interface ToolbarState {
    search?: string | null
    orderBy?: DiscoTableOrderByOption | null
    filterBy?: Record<string, DiscoTableFilterByOption> | null
  }

  export type ToolbarStateHandlers<T extends ToolbarState> = {
    handleSearchChange: (value: string) => void
    /** optionally provide a direction, or it will be toggled in the opposite direction when the same field is passed twice */
    handleOrderBy: (
      value: Omit<NonNullable<T["orderBy"]>, "direction"> & {
        direction?: NonNullable<T["orderBy"]>["direction"] | null
      }
    ) => void
    handleFilterBy: (value: {
      value: string
      option: ValueOf<T["filterBy"]> | null
    }) => void
    handleRemoveFilter: (field: string) => void
  }

  type TableState<T extends ToolbarState> = ToolbarStateHandlers<T> & {
    activePage: number
    setActivePage: React.Dispatch<React.SetStateAction<number>>
    toolbarState: T
    setToolbarState: React.Dispatch<React.SetStateAction<T>>
  }

  export function useTableState<T extends ToolbarState>(
    args: UseTableStateArgs<T>
  ): TableState<T> {
    const [activePage, setActivePage] = useState(args.initialActivePage ?? 0)
    const [toolbarState, setToolbarState] = useState<T>(args.initialToolbarState)
    const handleSearchChange = useDebounce(handleSearch, 500)

    const handleOrderBy: ToolbarStateHandlers<T>["handleOrderBy"] = (value) => {
      setToolbarState((prev) => {
        // use an explicit direction if provided, cycle a previously selected direction from ASC -> DESC -> remove order (NULL), or default to ASC
        const direction =
          value.direction ??
          (prev.orderBy?.field === value.field
            ? prev.orderBy?.direction === "ASC"
              ? "DESC"
              : prev.orderBy?.direction === "DESC"
              ? null
              : "ASC"
            : "ASC")

        return {
          ...prev,
          orderBy: direction ? { ...value, direction } : null,
        }
      })
    }

    const handleFilterBy: ToolbarStateHandlers<T>["handleFilterBy"] = ({
      value,
      option,
    }) => {
      setToolbarState((prev) => ({
        ...prev,
        filterBy: {
          ...prev.filterBy,
          [value]: option,
        },
      }))
    }

    const handleRemoveFilter: ToolbarStateHandlers<T>["handleRemoveFilter"] = (field) => {
      setToolbarState((prev) => {
        const filterBy = { ...prev.filterBy }
        delete filterBy[field]
        return {
          ...prev,
          filterBy,
        }
      })
    }

    return {
      activePage,
      setActivePage,
      toolbarState,
      setToolbarState,
      handleSearchChange,
      handleOrderBy,
      handleFilterBy,
      handleRemoveFilter,
    }

    function handleSearch(value: string) {
      setToolbarState((prev) => ({
        ...prev,
        search: value,
      }))
    }
  }

  type FilterState = Partial<ContentLabelFilterDropdownContentState> &
    Partial<ProductFilterDropdownContentState> &
    Partial<AppFilterDropdownContentState> &
    Partial<CollectionFilterDropdownContentState>

  /**
   * Handle deleting a filter tag from the toolbar state.
   */
  export function handleDeleteFilterTag<T extends FilterState, P extends SelectOption>(
    toolbarState: T,
    setToolbarState: (value: React.SetStateAction<Partial<T>>) => void,
    fieldName: keyof Partial<T>,
    tags: P[],
    tag: P
  ) {
    // If the field is removed from the array, or altogether, set to null
    const newValue = Array.isArray(toolbarState[fieldName])
      ? tags.filter((t) => t.value !== tag.value)
      : null

    // If the value is now null or an empty array, check if we also set an "all" state
    const shouldSetAllState = Array.isArray(newValue) ? !newValue.length : !newValue

    // If we should set the "all" state, we need to ensure it is set
    if (shouldSetAllState) {
      switch (fieldName) {
        case "contentLabels":
          toolbarState.allContentLabels = { value: "all", title: "All" }
          break
        case "products":
        case "allCommunity":
          toolbarState.allProducts = { value: "all", title: "All" }
          break
        case "apps":
          toolbarState.allApps = { value: "all", title: "All" }
          break
        case "collections":
          toolbarState.allCollections = { value: "all", title: "All" }
          break
      }
    }

    setToolbarState({ ...toolbarState, [fieldName]: newValue })
  }
}
